import { css } from "@emotion/css";
import { Divider } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { Repository } from "@octopusdeploy/octopus-server-client";
import type { GitHubAppConnections, GitHubAppInstallations } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { PageContent } from "~/components/PageContent/PageContent";
import { GitConnectionAnalyticView } from "./Analytics/GitConnectionAnalyticView";
import { GitHubAppAuthCheck } from "./GitHubAppAuthCheck";
import GitHubAppConnectionList from "./GitHubAppConnectionList";
import { GitConnectionAuthorizeOnboarding, GitConnectionInstallOnboarding } from "./Onboarding/GitConnectionOnboarding";
import { ConfigureGitHubAppLink } from "./Onboarding/GitHubAppLinks";
interface GitConnectionsPageProps {
    spaceId: string;
    loaderData: GitConnectionsPageLoaderData;
}
interface GitConnectionsPageLoaderData {
    connections: GitHubAppConnections;
    notConnectedInstallations: GitHubAppInstallations | undefined;
    userIsAuthorized: boolean;
}
export async function gitConnectionsPageLoader(repository: Repository): Promise<GitConnectionsPageLoaderData> {
    const connections = repository.GitHubApp.getConnections(0, Repository.takeAll);
    const authorizationStatus = await repository.GitHubApp.getAuthorizationStatus();
    const notConnectedInstallations = authorizationStatus.IsAuthorized ? await repository.GitHubApp.getInstallations(true) : undefined; // Skips any installations that are already connected.
    return { connections: await connections, userIsAuthorized: authorizationStatus.IsAuthorized, notConnectedInstallations };
}
export function GitConnectionsPage({ spaceId, loaderData }: GitConnectionsPageProps) {
    const { connections, notConnectedInstallations, userIsAuthorized } = loaderData;
    if (connections.Connections.length === 0) {
        // If we don't have any existing connections we want to show some sort of full-screen onboarding
        // because it's likely this is the first time they've hit this page. This covers all of the cases
        // where the generic list handling in the main return would leave users a little bit lost.
        // If we have no connections or installations, show the installation onboarding
        if (!userIsAuthorized) {
            // If we have no existing connections and the user is not authorized, show the authorize onboarding.
            return <GitConnectionAuthorizeOnboarding />;
        }
        if (notConnectedInstallations === undefined) {
            // If we have no connections, and the user is authorized but haven't finished loading the installations
            // from GitHub, don't show anything yet.
            return null;
        }
        if (notConnectedInstallations.Installations.length === 0) {
            // If we have no connections or installations, show the installation onboarding
            return (<PageContent header={{ title: "GitHub Connections" }}>
                    <BorderedContent>
                        <GitConnectionInstallOnboarding />
                    </BorderedContent>
                </PageContent>);
        }
    }
    return (<PageContent header={{ title: "GitHub Connections" }} callout={{ type: "custom", content: <GitHubAppAuthCheck actionDescription={"view other organisations and accounts"}/> }}>
            <BorderedContent>
                <GitConnectionAnalyticView name="View GitHub connections list"/>
                <GitHubAppConnectionList spaceId={spaceId} connections={connections} notConnectedInstallations={notConnectedInstallations}/>
                <Divider />
                <GitConnectionsInstallFooter userIsAuthorized={userIsAuthorized}/>
            </BorderedContent>
        </PageContent>);
}
interface GitConnectionsInstallFooterProps {
    userIsAuthorized: boolean;
}
function GitConnectionsInstallFooter({ userIsAuthorized }: GitConnectionsInstallFooterProps) {
    const styles = {
        footer: css({
            marginLeft: space[16],
            marginRight: space[16],
            marginTop: space[24],
            marginBottom: space[24],
            textAlign: "center",
        }),
    };
    if (!userIsAuthorized) {
        return null;
    }
    return (<div className={styles.footer}>
            <p>Install the GitHub app to connect to other organizations or users</p>
            <p>
                <ConfigureGitHubAppLink />
            </p>
        </div>);
}
